import { useWebchatHistory } from '@/hooks/use-webchat-history/use-webchat-history';
import { getWebchatMessages } from '@/services/webchat-service/webchat.service';
import { useRecordingUrl } from '@/hooks/use-recording-url/use-recording-url';
import { useWebchatStore } from '@/store/webchat.store';
import {
  CONVERSATION_TYPES,
  ConversationType,
  getVisitorLabel,
  isAiCallSession,
  WEBCHAT_STATUS_COLORS,
  WEBCHAT_STATUSES,
  WebchatConversation,
  WebchatHistoryParams,
  WebchatMessage,
  WebchatStatus,
} from '@/services/webchat-service/webchat.service.types';
import Icon, { TranslationOutlined } from '@ant-design/icons';
import { RecordingRow } from '../recording-row/recording-row';
import { getConversationChannel } from '@/utils/conversation-channel';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import Search from '@/assets/icons/search-md.svg?react';
import { Button, Descriptions, Drawer, Flex, Input, Spin, Table, Tag, Tooltip } from 'antd';
import Dropdown from '@/components/form/dropdown/dropdown';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatBubble } from '../chat-bubble/chat-bubble';
import { LiveTranscriptBubble } from '../live-transcript-bubble/live-transcript-bubble';
import styles from './history-panel.module.scss';

const LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇺🇸',
  'en-us': '🇺🇸',
  'en-gb': '🇬🇧',
  es: '🇪🇸',
  'es-mx': '🇲🇽',
  'es-es': '🇪🇸',
  pl: '🇵🇱',
  'pl-pl': '🇵🇱',
  fr: '🇫🇷',
  'fr-fr': '🇫🇷',
  de: '🇩🇪',
  pt: '🇵🇹',
  'pt-br': '🇧🇷',
  zh: '🇨🇳',
  ja: '🇯🇵',
  ko: '🇰🇷',
};

export const HistoryPanel = () => {
  const { t } = useTranslation(['webchat', 'common']);
  // Per-field selectors — see WebchatHeader / ChatPanel for the
  // rationale. With both Live and History panels staying mounted (see
  // webchat.tsx), History needs to be just as quiet on store updates
  // as Live.
  const setActiveTab = useWebchatStore((s) => s.setActiveTab);
  const setSelectedConversationId = useWebchatStore((s) => s.setSelectedConversationId);
  const showOriginalTranscript = useWebchatStore((s) => s.showOriginalTranscript);
  const toggleShowOriginalTranscript = useWebchatStore((s) => s.toggleShowOriginalTranscript);
  const [params, setParams] = useState<WebchatHistoryParams>({
    page: 1,
    per_page: 25,
  });
  const [drawerConversation, setDrawerConversation] = useState<WebchatConversation | null>(null);
  const [drawerMessages, setDrawerMessages] = useState<WebchatMessage[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const { historyData, isPending } = useWebchatHistory(params);
  const isDrawerAiCall = drawerConversation ? isAiCallSession(drawerConversation) : false;
  // Recording playback applies to any voice-flavored conversation type. The
  // useRecordingUrl hook already handles "no recording yet / still
  // processing / unavailable" cleanly so it's safe to fire on live calls
  // too — the row just shows a friendly status. AgentCall typically holds
  // category :agent; AI/legacy RAI hold category :ai; bridge is its own
  // post-claim recording on ElevenLabsCall.
  const isVoiceLike = ['ElevenLabsCall', 'AnalyticsCall', 'AgentCall'].includes(drawerConversation?.type ?? '');
  const recordingsEnabled = !!drawerConversation && isVoiceLike;
  const {
    recordingUrl: aiRecordingUrl,
    recordingStatus: aiRecordingStatus,
    refetchRecordingUrl: refetchAiRecording,
  } = useRecordingUrl({
    conversationId: drawerConversation?.id,
    category: 'ai',
    enabled: recordingsEnabled,
  });
  const {
    recordingUrl: bridgeRecordingUrl,
    recordingStatus: bridgeRecordingStatus,
    refetchRecordingUrl: refetchBridgeRecording,
  } = useRecordingUrl({
    conversationId: drawerConversation?.id,
    category: 'bridge',
    enabled: recordingsEnabled,
  });

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setParams((prev) => ({
      ...prev,
      page: pagination.current || 1,
      per_page: pagination.pageSize || 25,
    }));
  };

  const handleSearch = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setParams((prev) => ({ ...prev, query: value || undefined, page: 1 }));
  };

  const handleStatusFilter = (value: string | undefined) => {
    setParams((prev) => ({
      ...prev,
      status: (value as WebchatStatus) || undefined,
      page: 1,
    }));
  };

  const handleChannelFilter = (value: string | undefined) => {
    setParams((prev) => ({
      ...prev,
      channel: (value as ConversationType) || undefined,
      page: 1,
    }));
  };

  const jumpToLiveConversation = (record: WebchatConversation) => {
    setSelectedConversationId(record.id);
    setActiveTab('live');
  };

  const openTranscript = useCallback(async (conversation: WebchatConversation) => {
    setDrawerConversation(conversation);
    if (isAiCallSession(conversation)) {
      setDrawerMessages([]);
      return;
    }
    setDrawerLoading(true);
    try {
      const msgs = await getWebchatMessages(conversation.id);
      setDrawerMessages(msgs);
    } catch {
      setDrawerMessages([]);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const columns: ColumnsType<WebchatConversation> = [
    {
      title: t('webchat:fields.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>#{id}</span>,
    },
    {
      // Channel column — shows what kind of conversation this is (chat /
      // voice / SMS / email / agent call). The rep was previously left to
      // guess from the SID prefix or visitor phone field; this makes it
      // explicit and matches the icon shown on the Live tab session cards.
      title: t('webchat:fields.channel'),
      key: 'channel',
      width: 120,
      render: (_, record) => {
        const channel = getConversationChannel(record.type);
        const Icon = channel.icon as React.ComponentType<{ style?: React.CSSProperties }>;
        return (
          <Tag color={channel.color}>
            <Icon style={{ marginRight: 4 }} />
            {channel.label}
          </Tag>
        );
      },
    },
    {
      title: t('webchat:fields.visitor'),
      key: 'visitor',
      render: (_, record) => getVisitorLabel(record),
    },
    {
      title: t('webchat:fields.status'),
      dataIndex: 'webchat_status',
      key: 'webchat_status',
      width: 100,
      render: (status: WebchatStatus) => (
        <Tag color={WEBCHAT_STATUS_COLORS[status]}>{t(`webchat:status.${status}`)}</Tag>
      ),
    },
    {
      title: t('webchat:fields.agent'),
      dataIndex: 'assigned_agent_name',
      key: 'agent',
      render: (name: string | null) =>
        name ? name : <span className={styles.muted}>{t('webchat:fields.unassigned')}</span>,
    },
    {
      title: t('webchat:fields.language'),
      dataIndex: 'language',
      key: 'language',
      width: 90,
      render: (lang: string | null | undefined) => {
        if (!lang) return <span className={styles.muted}>—</span>;
        const flag = LANGUAGE_FLAGS[lang.toLowerCase()] ?? '🌐';
        return (
          <Tooltip title={lang}>
            <span style={{ fontSize: 16 }}>{flag}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('webchat:fields.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(date)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(date)}</p>
          </Flex>
        </Flex>
      ),
    },
  ];

  return (
    <div className={styles.historyPanel}>
      <div className={styles.filters}>
        <Dropdown
          className={styles.dropdown}
          wrapperClassName={styles.wrapperClassName}
          onChange={handleStatusFilter}
          placeholder={t('webchat:historyPanel.filters.allStatuses')}
          options={WEBCHAT_STATUSES.map((s) => ({ value: s, label: t(`webchat:status.${s}`) }))}
          value={params.status}
        />
        <Dropdown
          className={styles.dropdown}
          wrapperClassName={styles.wrapperClassName}
          onChange={handleChannelFilter}
          placeholder={t('webchat:historyPanel.filters.allChannels')}
          // Same labels (getConversationChannel) as the badges shown in the table column.
          options={CONVERSATION_TYPES.map((type) => ({
            value: type,
            label: getConversationChannel(type).label,
          }))}
          value={params.channel}
        />
        <Flex vertical gap={5} className={styles.searchContainer}>
          <Input
            onChange={handleSearch}
            value={params.query || ''}
            placeholder={t('common:actions.search')}
            prefix={<Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />}
          />
        </Flex>
      </div>
      <Table
        className={styles.table}
        columns={columns}
        dataSource={historyData?.conversations}
        rowKey="id"
        loading={isPending}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: historyData?.page || 1,
          pageSize: historyData?.per_page || 25,
          total: historyData?.total || 0,
          position: ['bottomCenter'],
          pageSizeOptions: ['12', '25', '50'],
          showSizeChanger: true,
          locale: { items_per_page: '' },
          nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
          prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => openTranscript(record),
          style: { cursor: 'pointer' },
        })}
      />

      <Drawer
        title={t('webchat:historyPanel.drawer.title')}
        open={!!drawerConversation}
        onClose={() => {
          setDrawerConversation(null);
          setDrawerMessages([]);
        }}
        width={480}
      >
        {drawerConversation && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('webchat:fields.id')}>
                #{drawerConversation.id}
                {drawerConversation.sid && (
                  <span style={{ marginLeft: 8, color: '#999', fontFamily: 'monospace' }}>
                    {drawerConversation.sid}
                  </span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('webchat:fields.visitor')}>
                {getVisitorLabel(drawerConversation)}
              </Descriptions.Item>
              <Descriptions.Item label={t('webchat:fields.agent')}>
                {drawerConversation.assigned_agent_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('webchat:fields.status')}>
                <Tag color={WEBCHAT_STATUS_COLORS[drawerConversation.webchat_status]}>
                  {t(`webchat:status.${drawerConversation.webchat_status}`)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('webchat:fields.created')}>
                {dayjs(drawerConversation.created_at).format('D MMM YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>
            <Flex gap={8} style={{ marginBottom: 12 }} wrap="wrap">
              {drawerConversation.webchat_status !== 'closed' && (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    jumpToLiveConversation(drawerConversation);
                    setDrawerConversation(null);
                    setDrawerMessages([]);
                  }}
                >
                  {t('webchat:historyPanel.drawer.jumpToLive')}
                </Button>
              )}
              {isAiCallSession(drawerConversation) && (
                <Tooltip
                  title={
                    showOriginalTranscript
                      ? t('webchat:historyPanel.drawer.showingOriginal')
                      : t('webchat:historyPanel.drawer.showingEnglish')
                  }
                >
                  <Button
                    type={showOriginalTranscript ? 'primary' : 'default'}
                    size="small"
                    icon={<TranslationOutlined />}
                    onClick={toggleShowOriginalTranscript}
                    aria-label={t('webchat:historyPanel.drawer.toggleTranslation')}
                  >
                    {showOriginalTranscript
                      ? t('webchat:historyPanel.drawer.original')
                      : t('webchat:historyPanel.drawer.english')}
                  </Button>
                </Tooltip>
              )}
            </Flex>
          </>
        )}
        {drawerLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : isVoiceLike ? (
          <div className={styles.transcriptMessages}>
            {/* Voice-flavored conversations (AI call, legacy RAI voice,
                human-handled AgentCall) all surface AI/Bridge recordings.
                The AI category covers both ElevenLabs Convai recordings and
                Twilio voice recordings of the legacy RAI flow; the Bridge
                category is specific to ElevenLabsCall after a human claim. */}
            <RecordingRow
              label={
                isDrawerAiCall
                  ? t('webchat:historyPanel.recordings.ai')
                  : t('webchat:historyPanel.recordings.voice')
              }
              url={aiRecordingUrl}
              status={aiRecordingStatus}
              onRefresh={() => refetchAiRecording()}
              conversationId={drawerConversation!.id}
              category="ai"
            />
            {isDrawerAiCall && (
              <RecordingRow
                label={t('webchat:historyPanel.recordings.liveAgent')}
                url={bridgeRecordingUrl}
                status={bridgeRecordingStatus}
                onRefresh={() => refetchBridgeRecording()}
                conversationId={drawerConversation!.id}
                category="bridge"
              />
            )}
            {/* AnalyticsCall now populates live_transcript_turns the same
                way ElevenLabsCall does (each chat_interaction triggers
                AddChatInteractionService#append_live_transcript_turns on
                the CRM), so the post-close History view should render
                them too — not just for AI calls. */}
            {(isDrawerAiCall || drawerConversation!.type === 'AnalyticsCall') ? (
              <>
                {(drawerConversation!.live_transcript_turns ?? []).map((turn, idx) => (
                  <LiveTranscriptBubble key={`${turn.received_at}-${idx}`} turn={turn} />
                ))}
                {(drawerConversation!.live_transcript_turns ?? []).length === 0 && (
                  <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                    {t('webchat:historyPanel.empty.noTranscript')}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                {t('webchat:historyPanel.empty.noPerTurnTranscript')}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.transcriptMessages}>
            {drawerMessages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}
            {drawerMessages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                {t('webchat:historyPanel.empty.noMessages')}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};
